import { useEffect, useState } from 'react';
import { be1Fetch } from '../config/api';

/**
 * 로그인한 사람이 쓸 수 있는 기능 — 서버 GET /api/capabilities.
 * 화면은 이것으로 툴박스·메뉴를 정할 뿐이고, 판정은 서버가 저장·배포·실행 때 다시 한다(services/shell_policy).
 */
export interface Capabilities {
  /** 외부명령어·DAG 코드 수정본 — mode: off | granted | on, reason: 막힌 이유 오류 코드 */
  /** allowed: 명령 직접 입력, scripts: 등록 스크립트(승인 폴더) */
  shell: { mode: string; allowed: boolean; reason: string | null; scripts?: boolean; scriptsReason?: string | null };
  admin: boolean;
  /** 연결을 이 앱에서 만든다(무설치판). false 면 Airflow 에서 만든다 */
  localConnections?: boolean;
  /** 관리자에게만 — 바꿔야 할 보안 설정 코드 (서버 services/security_check, 문구 layout:security.<코드>) */
  warnings?: string[];
}

let cache: { at: number; p: Promise<Capabilities | null> } | null = null;
const TTL = 60_000;

/** 같은 화면의 여러 곳이 동시에 불러도 요청은 하나 — 1분 동안 재사용(로그인이 바뀌면 화면이 새로 뜬다) */
export function loadCapabilities(force = false): Promise<Capabilities | null> {
  if (!force && cache && Date.now() - cache.at < TTL) return cache.p;
  const p = be1Fetch('/api/capabilities')
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  cache = { at: Date.now(), p };
  return p;
}

/** 모르는 동안(null)은 막지 않는다 — 깜박임 없이, 막는 것은 서버가 한다 */
export function useCapabilities(): Capabilities | null {
  const [caps, setCaps] = useState<Capabilities | null>(null);
  useEffect(() => {
    let alive = true;
    loadCapabilities().then((c) => { if (alive) setCaps(c); });
    return () => { alive = false; };
  }, []);
  return caps;
}

/** 외부명령어를 **어느 형태로도** 쓸 수 없을 때 그 이유 → 화면 안내 문구 키 (workflow:toolbox.*) */
export function shellBlockedKey(caps: Capabilities | null): string | null {
  if (!caps || caps.shell.allowed || caps.shell.scripts) return null;
  if (caps.shell.reason === 'trial.shellBlocked') return 'workflow:toolbox.shellTrial';
  if (caps.shell.reason === 'shell.notGranted') return 'workflow:toolbox.shellNotGranted';
  return 'workflow:toolbox.shellOff';
}
